import React, { useState, useMemo, useCallback, useRef } from 'react';
import { FormControlLabel, LinearProgress, Radio, RadioGroup, Button } from '@material-ui/core';
import { useDropzone } from 'react-dropzone';
import { Attachment, RemoveCircleRounded } from '@material-ui/icons';
import { getPreSignedUrl } from '../../../../../utils/PropertyAPIUtils';
import {
  abortMultipartUpload,
  bytesToMB,
  calculateFileChecksum,
  calculateTotalChunks,
  checkPDFHeader,
  CHUNK_SIZE,
  completeMultipartUpload,
  convertFileToBase64,
} from '../../../../../utils/commonUtils';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { getProcessedImages } from '../../../../../shared/image-upload/ImageProcessor';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import './Menu-File-Upload.scss';
import { isEmpty } from 'lodash';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { ENVIRONMENT, PROPERTY_SERVICE_URL } from '../../../../../env/Configurations';
import {
  IMAGE,
  MAX_UPLOADING_IMAGE_SIZE,
  MENU_CONFIG_MAX_PDF_SIZE,
  MIN_FILE_SIZE,
  PDF,
} from '../../../../../utils/Constants';

const baseStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'center',
  borderWidth: 1,
  borderRadius: 10,
  borderColor: '#CCCCCC',
  borderStyle: 'dashed',
  backgroundColor: '#fff',
  color: '#CCCCCC',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  minHeight: 95,
};

const activeStyle = {
  borderColor: '#03E67C',
};

const acceptStyle = {
  borderColor: '#03E67C',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const MenuFileUpload = ({ groupId, propertyType, createIrdMenu, onViewToggle, hotelId }) => {
  const [type, setType] = useState(PDF);
  const abortControllerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [upload, setUpload] = useState(false);
  const [acceptedFile, setAcceptedFile] = useState(null);
  const [uploadError, setUploadError] = useState(false);
  const [document, setDocument] = useState({
    url: '',
    type: '',
    name: '',
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const onDropAccepted = useCallback(
    (acceptedFiles) => {
      setUpload(true);
      if (acceptedFiles && acceptedFiles?.length > 0) {
        handleDroppredFile(type === PDF ? acceptedFiles[0] : acceptedFiles);
      }
    },
    [type],
  );

  const onDropRejected = useCallback((rejectedFiles) => {
    if (rejectedFiles?.length > 0) {
      setAcceptedFile(rejectedFiles[0]);
      setUploadError(true);
      notificationManager('danger', 'Invalid file type. Please upload a valid file.');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } =
    useDropzone({
      accept: type === PDF ? 'application/pdf' : 'image/png, image/jpg, image/jpeg',
      multiple: false,
      noClick: true,
      onDropAccepted,
      onDropRejected,
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept],
  );

  useDropzone({
    accept: type === PDF ? 'application/pdf' : 'image/png, image/jpg, image/jpeg',
    multiple: type !== PDF,
    onDropAccepted,
  });

  const createFileFromBase64 = (source, fileName) => {
    var arr = source.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  const dataURLtoFile = (dataurl, filename, imgUrl) => {
    return new Promise((resolve) => {
      const generatedFile = createFileFromBase64(dataurl, filename);
      resolve({ file: generatedFile, imgUrl: imgUrl });
    });
  };

  const handleDroppredFile = async (fileObj) => {
    setAcceptedFile(fileObj);
    setSelectedImages([]);
    setProgress(0);
    setUpload(true);
    setDocument({ url: '', type: '', name: '' });
    setUploadError(false);
    const fileSize = fileObj?.size;
    if (type === PDF) {
      if (fileSize > MENU_CONFIG_MAX_PDF_SIZE) {
        setUploadError(true);
        setUpload(false);
        notificationManager('danger', 'File size exceeds the maximum limit.');
        return;
      }
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
      }
      abortControllerRef.current = new AbortController();

      if (fileObj && fileSize < MIN_FILE_SIZE) {
        convertFileToBase64(fileObj)
          .then(async (base64) => {
            const input = {
              contentLength: fileSize,
              contentType: fileObj.type,
              filename: fileObj.name,
              isDocUpload: true,
              groupId,
              propertyType,
              type: 'docs',
              contents: base64,
            };
            try {
              const res = await getPreSignedUrl(input);
              await checkPDFHeader(fileObj);
              setProgress(50);
              if (res?.data?.getPreSignUrlCMS?.key) {
                setProgress(100);
                setDocument({
                  url: res?.data?.getPreSignUrlCMS?.key,
                  type: 'S3',
                  name: fileObj.name,
                });
                setAcceptedFile(null);
                setUpload(false);
                setUploadError(false);
              } else {
                setUpload(false);
                setUploadError(true);
                setDocument({ url: '', type: '', name: '' });
              }
            } catch (exception) {
              setUpload(false);
              setUploadError(true);
              setDocument({ url: '', type: '', name: '' });
              if (ENVIRONMENT === 'dev') {
                console.log(exception?.message);
              }

              if (exception?.message) {
                notificationManager('danger', exception?.message.replace('GraphQL error:', ''));
              } else {
                notificationManager('danger', 'Error while uploading file.');
              }
            }
          })
          .catch((error) => {
            if (ENVIRONMENT === 'dev') {
              console.log(error?.message, 'error');
            }

            notificationManager('danger', 'Error converting file to base64');
          });
      } else {
        const totalChunks = calculateTotalChunks(fileSize);
        let uploadId = null;
        const fileChecksum = await calculateFileChecksum(fileObj);

        try {
          const startUploadResponse = await fetch(
            `${PROPERTY_SERVICE_URL}/startMultipartUpload`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              signal: abortControllerRef?.current?.signal,
              body: JSON.stringify({
                hotelId,
                groupId,
                type: 'docs',
                propertyType,
                contentType: fileObj?.type,
                filename: fileObj?.name,
                IsCMSRequest: true,
                fileSize,
                fileChecksum,
              }),
            },
          );

          if (!startUploadResponse?.ok) {
            throw new Error(`Failed to start upload: ${startUploadResponse?.status}`);
          }

          const chunkData = await startUploadResponse.json();
          uploadId = chunkData?.data?.key;

          try {
            for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
              const start = chunkIndex * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, fileSize);
              const fileChunk = fileObj?.slice(start, end);

              const base64Chunk = await convertFileToBase64(fileChunk);

              const response = await fetch(`${PROPERTY_SERVICE_URL}/uploadMultipartChunk`, {
                method: 'POST',
                headers: {
                  Referer: 'http://localhost:3000/',
                  'Content-Type': 'application/json',
                },
                signal: abortControllerRef?.current?.signal,
                body: JSON.stringify({
                  groupId,
                  hotelId,
                  uploadId,
                  sequenceNumber: chunkIndex + 1,
                  contents: base64Chunk,
                }),
              });

              if (!response.ok) {
                throw new Error(`Failed to upload chunk ${chunkIndex + 1}: ${response?.status}`);
              }
              const progressPercentage = ((chunkIndex + 1) / totalChunks) * 100;
              setProgress(progressPercentage);
            }

            const completeResponse = await completeMultipartUpload(groupId, hotelId, uploadId);

            if (completeResponse?.status === 'ok' && completeResponse?.data?.status === true) {
              const imageUrl = completeResponse?.data?.message;
              if (imageUrl) {
                setProgress(100);
                setDocument({
                  url: imageUrl,
                  type: 'S3',
                  name: fileObj?.name,
                });
                setAcceptedFile(null);
                setUpload(false);
                setUploadError(false);
              } else {
                setUpload(false);
                setUploadError(true);
                setDocument({ url: '', type: '', name: '' });
              }
            } else {
              setUpload(false);
              setUploadError(true);
              setDocument({ url: '', type: '', name: '' });
              console.error('File upload failed:', completeResponse);
              throw new Error('Failed to complete upload');
            }
          } catch (error) {
            console.error('Error during file upload:', error);
            setUploadError(true);
            notificationManager('danger', error?.message || 'Error while uploading file.');

            if (uploadId) {
              await abortMultipartUpload(uploadId, groupId, hotelId);
            }
          }
        } catch (error) {
          console.error('Error in multipart upload:', error);
          setUploadError(true);
          notificationManager('danger', error?.message || 'Error while uploading file.');
        }
      }
    } else if (type === IMAGE) {
      fileObj?.forEach((file, index) => {
        if (file?.size < MAX_UPLOADING_IMAGE_SIZE) {
          try {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
              var imageObj = new Image();
              imageObj.src = reader.result;
              imageObj.onload = async () => {
                const [master, ratio1to1, ratio16to9, ratio21to9] = await Promise.all([
                  dataURLtoFile(reader.result, 'master', imageObj.src),
                  dataURLtoFile(reader.result, 'ratio1to1', imageObj.src),
                  dataURLtoFile(reader.result, 'ratio16to9', imageObj.src),
                  dataURLtoFile(reader.result, 'ratio21to9', imageObj.src),
                ]);

                setSelectedImages((prev) => {
                  return [
                    ...prev,
                    {
                      fileName: file.name,
                      index,
                      isTouched: true,
                      fileData: {
                        master,
                        ratio1to1,
                        ratio16to9,
                        ratio21to9,
                      },
                    },
                  ];
                });
              };
            });

            reader.readAsDataURL(file);
          } catch (e) {
            setAcceptedFile(null);
            setSelectedImages([]);
            setUploadError(true);
            setUpload(false);
            notificationManager('danger', 'Error while uploading image.');
          }
        } else {
          setAcceptedFile(null);
          setSelectedImages([]);
          setUploadError(true);
          setUpload(false);
          notificationManager('danger', 'Image size exceeds the maximum limit.');
          return;
        }
      });
    }
  };

  const onUploadedDocRemove = (type) => {
    setUploadError(false);
    setDocument({ url: '', type: '', name: '' });
    setType(type);
    if (type === PDF) {
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
      }
      setUploadError(false);
      setSelectedImages([]);
      setUpload(false);
    }
  };

  const onAddFile = async () => {
    if (type === PDF) {
      await createIrdMenu(document?.name, document?.url);
    } else {
      setLoading(true);
      let generatedImages = await getProcessedImages(
        selectedImages,
        groupId,
        propertyType,
        hotelId,
      );
      await createIrdMenu(selectedImages[0]?.fileName, '', generatedImages);
      setLoading(false);
    }
    setLoading(false);
    onViewToggle(false, null);
  };

  const onUploadedImageRemoval = (index) => {
    const images = [...selectedImages];
    images.splice(index, 1);
    setSelectedImages(images);
    if (isEmpty(images)) {
      setAcceptedFile(null);
      setUpload(false);
    }
  };

  const onChangeType = (e) => {
    setUploadError(false);
    setUpload(false);
    setDocument({ url: '', type: '', name: '' });
    setAcceptedFile(null);
    setType(e?.target?.value);
    if (e?.target?.value === PDF) {
      setSelectedImages([]);
    }
  };

  return (
    <div className='card ird-card shadow mx-5 mt-3'>
      <div className='ird-center-box'>
        <div className='centered-element'>
          <div className='upload-title'>Upload Menu</div>
        </div>
        <div className='type-selection col-md-6'>
          <RadioGroup
            row
            aria-label='doc-upload'
            onChange={(e) => onChangeType(e)}
            value={type}
            name={''}
            className='document-type mb-3'
          >
            <FormControlLabel
              value={PDF}
              control={<Radio color='primary' />}
              label={PDF}
              className='me-5'
            />
            <FormControlLabel
              value={IMAGE}
              control={<Radio color='primary' />}
              label='Image(s)'
              className='ms-4'
            />
          </RadioGroup>
        </div>
        {!isEmpty(document.url) && !uploadError && (
          <div className='row'>
            <div className='col-md-6 uploaded-doc-custom'>
              <div className='attached-doc d-flex justify-content-between p-3 align-items-center'>
                <div>
                  <div>
                    <DescriptionOutlinedIcon color='action' className='me-2' />
                    {document?.name}
                  </div>
                </div>
                <CancelIcon
                  className='pointer me-1 '
                  onClick={(e) => onUploadedDocRemove(PDF)}
                ></CancelIcon>
              </div>
            </div>
          </div>
        )}
        {
          // !acceptedFile && !
          !upload &&
            ((type === PDF && isEmpty(document?.url) && !uploadError) ||
              (type === IMAGE && selectedImages?.length < 1)) && (
              <div className='col-md-6 img-drop-zone-custom'>
                <div
                  className={uploadError ? 'error' : ''}
                  {...getRootProps({ style })}
                  onClick={open}
                >
                  <input {...getInputProps()} />
                  <div className='d-flex align-items-center'>
                    <Attachment />
                    <span className='ms-1'>
                      {`Drag and drop or `}
                      <span className='text-highlight pointer'>Browse</span>
                    </span>
                  </div>
                </div>
                {uploadError && type !== IMAGE && (
                  <div className='mt-1'>
                    Document Upload failed.{' '}
                    <span onClick={(e) => onUploadedDocRemove(PDF)} className='link-text text-red'>
                      Try Again
                    </span>
                  </div>
                )}
              </div>
            )
        }

        {upload && type === PDF && (
          <div className='d-flex justify-content-center align-items-center progress-bar-screen'>
            <div className='upload-progress p-3 d-flex justify-content-around'>
              <div>{acceptedFile?.name}</div>
              <div className='upload-progress-title'>Uploading</div>
              <div className='mt-2 w-25'>
                <LinearProgress variant='determinate' color='secondary' value={progress} />
              </div>
              <CancelIcon className='pointer me-1 ' onClick={(e) => onUploadedDocRemove(PDF)} />
            </div>
          </div>
        )}
        {uploadError && type === PDF && (
          <div className='d-flex justify-content-center flex-column align-items-center progress-bar-screen'>
            <div className='upload-progress-error p-3 d-flex justify-content-between'>
              <div>{acceptedFile?.name}</div>
              <div className='d-flex justify-content-start ms-5'>
                <div className='upload-progress-title me-2'>File Upload Error</div>
                <CancelIcon className=' pointer me-1 ' onClick={(e) => onUploadedDocRemove(PDF)} />
              </div>
            </div>
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <h6 className='mt-3'>
                Kindly ensure the document data meets the acceptance criteria and{' '}
                <span className='error-text pointer' onClick={open}>
                  Try Again
                </span>
              </h6>
            </div>
          </div>
        )}

        {uploadError && type === IMAGE && (
          <div className='d-flex justify-content-center flex-column align-items-center progress-bar-screen pt-4'>
            <div className='image-upload-failed'>
              <div className='image-error-icon'>
                <ErrorOutlineRoundedIcon fontSize='large' />
              </div>
              <div className='image-error-title'>Failed</div>
            </div>
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <h6 className='mt-3'>
                Kindly ensure the document data meets the acceptance criteria and{' '}
                <span className='error-text pointer' onClick={open}>
                  Try Again
                </span>
              </h6>
            </div>
          </div>
        )}

        {!uploadError && type === IMAGE && selectedImages?.length > 0 && (
          <div className='d-flex uploaded-images'>
            {selectedImages
              .sort((v1, v2) => v1.index - v2.index)
              .map((item, index) => (
                <div key={item?.fileName} className={`img-preview-cropped-img me-3 $`}>
                  <i
                    className='fa fa-times-circle'
                    onClick={() => {
                      onUploadedImageRemoval(index);
                    }}
                  ></i>
                  <img alt={index} src={item?.fileData?.master?.imgUrl} />
                  <div className='menu-image-name' title={item?.fileName}>
                    {item?.fileName}
                  </div>
                </div>
              ))}
          </div>
        )}

        <div className='d-flex justify-content-center confirm-button-custom pb-5'>
          <Button
            variant='outlined'
            type='button'
            color='secondary'
            size='large'
            className='doc-custom-btn me-4'
            onClick={() => onViewToggle(false, null)}
          >
            Cancel
          </Button>
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <Button
              variant='contained'
              color='primary'
              className='doc-custom-btn me-3'
              size='large'
              onClick={onAddFile}
              disabled={
                uploadError ||
                (type === PDF ? isEmpty(document?.name) : selectedImages?.length < 1) ||
                loading
              }
            >
              Add
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuFileUpload;
